import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "../../components/nuclear/button";
import { Input } from "../../components/nuclear/input";
import { Label } from "../../components/nuclear/label";
import "../../components/nuclear/ui.css";
import "./ForgotPasswordPage.module.css";
import { sendPasswordResetCode, submitPasswordReset } from "./authServices";
import { useAuthenticationPage } from "./authPageHelpers";
import { getPasswordValidationError, isValidEmail } from "./authValidation";

const RESEND_COOLDOWN_SECONDS = 60;

function RequiredMark() {
    return <span className={"auth-required-mark"} aria-label={"required"}>*</span>;
}

export default function ConfirmResetPasswordPage({ redirection }) {
    const dispatch = useDispatch();
    const [email, setEmail] = useState("");
    const [confirmationCode, setConfirmationCode] = useState("");
    const [codeSent, setCodeSent] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [isSubmittingReset, setIsSubmittingReset] = useState(false);
    const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const emailInputRef = useRef(null);
    const confirmationCodeInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const confirmPasswordInputRef = useRef(null);

    useAuthenticationPage(dispatch, redirection);

    useEffect(() => {
        document.title = "Reset Password - Animotive VR Dashboard";
        return () => {
            document.title = "Animotive VR Dashboard";
        };
    }, []);

    useEffect(() => {
        if (resendCooldownSeconds <= 0) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setResendCooldownSeconds((current) => Math.max(current - 1, 0));
        }, 1000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [resendCooldownSeconds]);

    async function requestCode(event) {
        event.preventDefault();
        setErrorMessage(null);
        setConfirmed(false);

        const trimmedEmail = email.trim();

        if (!trimmedEmail) {
            setErrorMessage("Email is required");
            emailInputRef.current?.focus();
            return;
        }

        if (!isValidEmail(trimmedEmail)) {
            setErrorMessage("Please enter a valid email address (e.g., user@example.com)");
            emailInputRef.current?.focus();
            return;
        }

        setIsSendingCode(true);
        try {
            await sendPasswordResetCode({ email: trimmedEmail });
            setEmail(trimmedEmail);
            setCodeSent(true);
            setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
        } catch (error) {
            setErrorMessage(error?.message ?? "Unable to send reset code.");
        } finally {
            setIsSendingCode(false);
        }
    }

    async function resendCode() {
        setErrorMessage(null);
        setIsSendingCode(true);

        try {
            await sendPasswordResetCode({ email: email.trim() });
            setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
            confirmationCodeInputRef.current?.focus();
        } catch (error) {
            setErrorMessage(error?.message ?? "Unable to resend reset code.");
        } finally {
            setIsSendingCode(false);
        }
    }

    async function confirmReset(event) {
        event.preventDefault();
        setErrorMessage(null);
        const password = passwordInputRef.current?.value ?? "";
        const confirmPassword = confirmPasswordInputRef.current?.value ?? "";

        const trimmedCode = confirmationCode.trim();
        if (!trimmedCode) {
            setErrorMessage("Confirmation code is required");
            confirmationCodeInputRef.current?.focus();
            return;
        }

        const passwordError = getPasswordValidationError(password);
        if (passwordError) {
            setErrorMessage(passwordError);
            passwordInputRef.current?.focus();
            return;
        }

        if (password !== confirmPassword) {
            setErrorMessage("Passwords do not match");
            confirmPasswordInputRef.current?.focus();
            return;
        }

        setIsSubmittingReset(true);
        try {
            await submitPasswordReset({
                email: email.trim(),
                confirmationCode: trimmedCode,
                password,
            });
            setConfirmed(true);
        } catch (error) {
            setErrorMessage(error?.message ?? "Unable to confirm password reset.");
        } finally {
            setIsSubmittingReset(false);
        }
    }

    const isResendDisabled = isSendingCode || isSubmittingReset || resendCooldownSeconds > 0;
    const resendButtonLabel = isSendingCode
        ? "Resending..."
        : resendCooldownSeconds > 0
            ? `Resend Code (${resendCooldownSeconds}s)`
            : "Resend Code";

    if (confirmed) {
        return (
            <div className={"auth-page-shell"}>
                <div className={"auth-page-container"}>
                    <img
                        src={"/img/Branding/AnimotiveLogoHeader.png"}
                        alt={"Animotive"}
                        className={"auth-logo"}
                    />

                    <div
                        role={"status"}
                        aria-live={"polite"}
                        aria-atomic={"true"}
                        className={"auth-success-alert"}
                    >
                        <CheckCircle size={22} className={"auth-alert-icon"} />
                        <div>
                            <h1 className={"auth-success-title"}>Password reset successful</h1>
                            <p className={"auth-success-copy"}>
                                Your password has been successfully reset. You can now log in with your new password.
                            </p>
                        </div>
                    </div>

                    <Button asChild={true} fullWidth={true}>
                        <RouterLink to={"/login"}>Back to Login</RouterLink>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={"auth-page-shell"}>
            <div className={"auth-page-container"}>
                <img
                    src={"/img/Branding/AnimotiveLogoHeader.png"}
                    alt={"Animotive"}
                    className={"auth-logo"}
                />

                <h1 className={"auth-forgot-heading"}>Reset your password</h1>
                <p className={"auth-forgot-copy"}>
                    {!codeSent
                        ? "Enter the email address for your account and we will send you a reset code."
                        : `Enter the code we sent to ${email} and choose a new password.`}
                </p>

                {errorMessage ? (
                    <div
                        id={"forgot-password-error"}
                        role={"alert"}
                        aria-live={"assertive"}
                        aria-atomic={"true"}
                        className={"auth-error-alert"}
                    >
                        <AlertCircle size={18} className={"auth-alert-icon"} />
                        <span>{errorMessage}</span>
                    </div>
                ) : null}

                {(isSendingCode || isSubmittingReset) ? (
                    <div role={"status"} aria-live={"polite"} className={"sr-only"}>
                        Processing, please wait...
                    </div>
                ) : null}

                {!codeSent ? (
                    <form onSubmit={requestCode} className={"auth-form-grid"} noValidate={true}>
                        <div>
                            <Label htmlFor={"forgot-password-email"} className={"auth-field-label"}>
                                Email
                                <RequiredMark />
                            </Label>
                            <Input
                                ref={emailInputRef}
                                id={"forgot-password-email"}
                                type={"email"}
                                name={"email"}
                                autoComplete={"email"}
                                value={email}
                                onChange={(event) => {
                                    setEmail(event.target.value);
                                    setErrorMessage(null);
                                }}
                                placeholder={"Enter your email"}
                                required={true}
                                aria-required={"true"}
                                aria-invalid={errorMessage ? "true" : "false"}
                                aria-describedby={errorMessage ? "forgot-password-error" : undefined}
                            />
                        </div>

                        <Button
                            type={"submit"}
                            disabled={isSendingCode}
                            aria-busy={isSendingCode ? "true" : "false"}
                            fullWidth={true}
                        >
                            {isSendingCode ? <Loader2 size={18} /> : null}
                            {isSendingCode ? "Sending..." : "Send Code"}
                        </Button>

                        <div className={"auth-link-row auth-link-row--center"}>
                            <RouterLink to={"/login"} className={"auth-link"}>
                                Back to login
                            </RouterLink>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={confirmReset} className={"auth-form-grid"} noValidate={true}>
                        <div>
                            <Label htmlFor={"forgot-password-code"} className={"auth-field-label"}>
                                Code
                                <RequiredMark />
                            </Label>
                            <Input
                                ref={confirmationCodeInputRef}
                                id={"forgot-password-code"}
                                type={"text"}
                                name={"confirmationCode"}
                                autoComplete={"one-time-code"}
                                value={confirmationCode}
                                onChange={(event) => {
                                    setConfirmationCode(event.target.value);
                                    setErrorMessage(null);
                                }}
                                placeholder={"Enter verification code"}
                                required={true}
                                aria-required={"true"}
                                aria-invalid={errorMessage ? "true" : "false"}
                                aria-describedby={errorMessage ? "forgot-password-error" : undefined}
                            />
                        </div>

                        <div>
                            <Label htmlFor={"forgot-password-new-password"} className={"auth-field-label"}>
                                New Password
                                <RequiredMark />
                            </Label>
                            <div className={"auth-password-wrap"}>
                                <Input
                                    ref={passwordInputRef}
                                    id={"forgot-password-new-password"}
                                    type={showPassword ? "text" : "password"}
                                    name={"newPassword"}
                                    autoComplete={"new-password"}
                                    onChange={() => setErrorMessage(null)}
                                    placeholder={"Enter new password"}
                                    required={true}
                                    aria-required={"true"}
                                    aria-invalid={errorMessage && !(passwordInputRef.current?.value ?? "") ? "true" : "false"}
                                    aria-describedby={errorMessage ? "forgot-password-error" : "forgot-password-note"}
                                    className={"auth-input-with-icon"}
                                />
                                <button
                                    type={"button"}
                                    onClick={() => setShowPassword((current) => !current)}
                                    aria-label={showPassword ? "Hide new password" : "Show new password"}
                                    aria-pressed={showPassword ? "true" : "false"}
                                    aria-controls={"forgot-password-new-password"}
                                    className={"auth-password-toggle"}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor={"forgot-password-confirm-password"} className={"auth-field-label"}>
                                Confirm Password
                                <RequiredMark />
                            </Label>
                            <div className={"auth-password-wrap"}>
                                <Input
                                    ref={confirmPasswordInputRef}
                                    id={"forgot-password-confirm-password"}
                                    type={showConfirmPassword ? "text" : "password"}
                                    name={"confirmPassword"}
                                    autoComplete={"new-password"}
                                    onChange={() => setErrorMessage(null)}
                                    placeholder={"Confirm new password"}
                                    required={true}
                                    aria-required={"true"}
                                    aria-invalid={errorMessage && (passwordInputRef.current?.value ?? "") !== (confirmPasswordInputRef.current?.value ?? "") ? "true" : "false"}
                                    aria-describedby={errorMessage ? "forgot-password-error" : "forgot-password-note"}
                                    className={"auth-input-with-icon"}
                                />
                                <button
                                    type={"button"}
                                    onClick={() => setShowConfirmPassword((current) => !current)}
                                    aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                    aria-pressed={showConfirmPassword ? "true" : "false"}
                                    aria-controls={"forgot-password-confirm-password"}
                                    className={"auth-password-toggle"}
                                >
                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <p id={"forgot-password-note"} className={"auth-forgot-note"}>
                            Password must be at least 8 characters and include uppercase, lowercase, and a number.
                        </p>

                        <div className={"auth-forgot-actions"}>
                            <Button
                                type={"button"}
                                variant={"outline"}
                                disabled={isResendDisabled}
                                aria-busy={isSendingCode ? "true" : "false"}
                                className={"auth-forgot-resend-button"}
                                onClick={resendCode}
                            >
                                {isSendingCode ? <Loader2 size={18} /> : null}
                                {resendButtonLabel}
                            </Button>
                            <Button
                                type={"submit"}
                                disabled={isSubmittingReset}
                                aria-busy={isSubmittingReset ? "true" : "false"}
                                fullWidth={true}
                            >
                                {isSubmittingReset ? <Loader2 size={18} /> : null}
                                {isSubmittingReset ? "Submitting..." : "Submit"}
                            </Button>
                        </div>

                        <div className={"auth-link-row auth-link-row--center"}>
                            <RouterLink to={"/login"} className={"auth-link"}>
                                Back to login
                            </RouterLink>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
